import { BatchStatus, Severity, SystemRole } from "node-library/dist/constants";
import { FrontendUserInfo } from "node-library/dist/portal";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonTextLoadingAnimation } from "../../../../common-components/animation/ButtonLoadingAnimation";
import { GreenButton } from "../../../../common-components/button";
import {
	CustomDatePicker,
	CustomDateTimePicker,
} from "../../../../common-components/datePicker";
import {
	CustomTextInput,
	RowDropdown,
} from "../../../../common-components/inputs";
import { ModalWithButtons } from "../../../../common-components/modals/modal";
import { RedErrorIcon } from "../../../../common-components/toast/errorIcons";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../../common-components/toast/successIcons";
import { sendPOSTRequest } from "../../../../requestHelper/sendRequest";

import { getBatchStatusTranslationString } from "../../../../helpers/testBatchHelper";

interface Props {
	user: FrontendUserInfo;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	batchId: string;
	batchStatus: BatchStatus;
	setBatchStatus: React.Dispatch<React.SetStateAction<BatchStatus>>;

	resultSubmitTime: Date | undefined;
	setResultSubmitTime: React.Dispatch<React.SetStateAction<Date | undefined>>;
	completeTime: Date | undefined;
	setCompleteTime: React.Dispatch<React.SetStateAction<Date | undefined>>;
	comments: string;
	setComments: React.Dispatch<React.SetStateAction<string>>;
}

export default function EditBatchStatusModal(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { batchId, batchStatus, open, setOpen } = props;

	const [currentBatchStatus, setCurrentBatchStatus] =
		useState<BatchStatus>(batchStatus);

	const [resultSubmitTime, setResultSubmitTime] = useState<Date | null>(
		props.resultSubmitTime || null
	);

	const [completeDate, setCompleteDate] = useState<Date | null>(
		props.completeTime || null
	);

	const [comments, setComments] = useState<string>(props.comments);

	const [loading, setLoading] = useState<boolean>(false);

	function changeBatchStatus(status: BatchStatus) {
		setCurrentBatchStatus(status);
		switch (status) {
			case BatchStatus.IN_PROGRESS:
				setResultSubmitTime(null);
				setCompleteDate(null);
				break;
			case BatchStatus.PENDING_RESULT_VALIDATION:
				setResultSubmitTime(new Date());
				setCompleteDate(null);
				break;
			case BatchStatus.COMPLETE:
				setResultSubmitTime(props.resultSubmitTime || null);
				setCompleteDate(new Date());
				break;
		}
	}

	const statusSelectionDropdown: React.ReactNode = (
		<RowDropdown
			id="order-status-selection-dropdown"
			className="order-status-selection-dropdown"
			label={translation("BatchStatus")}
			options={Object.values(BatchStatus).map((value) => {
				return {
					value: value,
					title: getBatchStatusTranslationString(value, translation),
				};
			})}
			value={currentBatchStatus}
			setValue={(status) => {
				changeBatchStatus(status);
			}}
		></RowDropdown>
	);

	const fieldsForDates: Array<any> = [
		{
			id: "result-submitted-date-picker",
			label: `${translation("ResultSubmittedOn")}`,
			value: resultSubmitTime,
			setValue: setResultSubmitTime,
			dateOnly: true,
			disabled: true,
		},

		{
			id: "order-complete-date-picker",
			label: `${translation("CompletedOn")}`,
			value: completeDate,
			setValue: setCompleteDate,
			dateOnly: true,
			disabled: true,
		},
	];

	async function handleSubmit() {
		try {
			setLoading(true);

			const data = await sendPOSTRequest(
				"/sample-test-batches/update-batch-status",
				{
					batchId: batchId,
					batchStatus: currentBatchStatus,
					resultSubmitTime,
					completeTime: completeDate,
					comments,
				}
			);

			props.setBatchStatus(currentBatchStatus);

			props.setResultSubmitTime(resultSubmitTime || undefined);
			props.setCompleteTime(completeDate || undefined);
			props.setComments(comments);

			openSnackbar(
				translation("BatchStatusUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
			setOpen(false);
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	}

	useEffect(() => {
		if (open) {
			setCurrentBatchStatus(batchStatus);
			setResultSubmitTime(props.resultSubmitTime || null);
			setCompleteDate(props.completeTime || null);
			setComments(props.comments);
		}
	}, [open]);

	const timePickers = fieldsForDates.map((field, index) => {
		return (
			<div key={index} className="modal-datepicker-row">
				<div className="typography-body-01">{field.label}</div>
				{field.dateOnly ? (
					<CustomDatePicker
						selectedDate={field.value}
						disabled={field.disabled}
						setSelectedDate={field.setValue}
						errorMsg={field.errorMsg}
					/>
				) : (
					<CustomDateTimePicker
						selectedDate={field.value}
						disabled={field.disabled}
						setSelectedDate={field.setValue}
						errorMsg={field.errorMsg}
					></CustomDateTimePicker>
				)}
			</div>
		);
	});

	const commentsInput: React.ReactNode = (
		<CustomTextInput
			id="new-sample-weight-input"
			key="new-sample-weight-input"
			label={`${translation("Comments")}`}
			value={comments}
			setValue={(value) => {
				setComments(value);
			}}
			// placeholder={translation("SampleDescriptionDescription")}
			required
			isTextArea
		/>
	);
	const updateStatusTimeModal: React.ReactNode = (
		<ModalWithButtons
			modalWidth={500}
			headerText={translation("BatchStatus")}
			headerDescription={translation("UpdateBatchStatusDescription")}
			open={open}
			setOpen={setOpen}
			buttons={[
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						margin: "0px 20px",
					}}
				>
					<GreenButton
						key="update-order-status-time-button"
						id="update-order-status-time-button"
						size="flex"
						children={
							loading ? (
								<ButtonTextLoadingAnimation
									text={translation("Loading")}
								></ButtonTextLoadingAnimation>
							) : (
								translation("Update")
							)
						}
						disabled={loading}
						onClick={handleSubmit}
					></GreenButton>
				</div>,
			]}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
				{statusSelectionDropdown}
				{timePickers}
				{commentsInput}
			</div>
		</ModalWithButtons>
	);
	return updateStatusTimeModal;
}
